<template>
  <div
    ref="root"
    class="wrapper"
    :style="rootStyles"
  >
    <SkeletonItem
      :class="contentClasses"
      :style="contentStyles"
    />
  </div>
</template>

<script setup lang="ts">
  import { SkeletonItem } from '@experience/core-skeleton-lib';

  import { computed, useTemplateRef } from 'vue';

  import type { SkeletonBreakpointsWidths } from '@/shared/ui/text/types';
  import { useScreenSize } from '@experience/shared';

  const props = defineProps<{
    breakpointsWidth?: string | SkeletonBreakpointsWidths;
    skeletonClass?: string;
  }>();

  defineSlots<{
    default(): unknown;
  }>();

  const rootRef = useTemplateRef('root');

  const { isMobile, isTablet, isDesktop } = useScreenSize();

  const contentClasses = computed(() => {
    return [props.skeletonClass, 'content'];
  });

  const widthStyle = computed(() => {
    if (!props.breakpointsWidth) {
      return undefined;
    }

    if (typeof props.breakpointsWidth === 'string') {
      return props.breakpointsWidth;
    }

    const desktop = props.breakpointsWidth?.desktop;
    const tablet = props.breakpointsWidth?.tablet;
    const mobile = props.breakpointsWidth?.mobile;

    if (isMobile.value && mobile) {
      return mobile;
    }

    if (isTablet.value && tablet) {
      return tablet;
    }

    if (isDesktop.value && desktop) {
      return desktop;
    }

    return props.breakpointsWidth?.default || undefined;
  });

  const getStyleValue = (style: string) => {
    return rootRef.value?.parentElement
      ? getComputedStyle(rootRef.value.parentElement).getPropertyValue(style)
      : undefined;
  };

  const rootStyles = computed(() => {
    const height = getStyleValue('line-height');

    const width = widthStyle.value;
    const minWidth = width;

    return {
      width,
      minWidth,
      height,
    };
  });

  const contentStyles = computed(() => {
    const height = getStyleValue('font-size');

    return {
      height,
    };
  });
</script>

<style lang="scss">
  .wrapper {
    display: flex;
    align-items: center;
  }

  .content {
    flex: 1;
    border-radius: 8px;
  }
</style>
